// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyEnquiry } from "@/types";
import { formatDateTime, pressToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip, { type TyChipTone } from "@/UI/atoms/StatusChip";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    enquiry: TyEnquiry;
    /** The whole tile opens it — everything that has to be read in full is read there. */
    onOpen: () => void;
    /** Creates the company from it, which is the one thing this queue is worked for. */
    onConvert: () => void;
};

/** Where it stands, as a colour. Gold for the one still owed, quiet for the one finished with. */
const TONES: Record<string, TyChipTone> = { new: "warn", read: "neutral", done: "dark" };

/**
 * One person asking for an account, as a tile in the queue.
 *
 * **The company's name leads, not the person's** — the opposite of a support tile, and for the same
 * reason that one puts the person first. A ticket is somebody's sentence about an account they
 * already have; this is a company that does not exist yet, and the question an admin is holding is
 * "who is this company" before "who is this person".
 */
export default function EnquiryCard({ enquiry, onOpen, onConvert }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    return (
        <article className="enquiry-tile" role="button" tabIndex={0} onClick={onOpen} onKeyDown={pressToOpen(onOpen)}>
            <header className="enquiry-tile__head">
                <Avatar name={enquiry.organizationName} size={40} bordered rounded />

                <div className="enquiry-tile__who">
                    <span className="enquiry-tile__company">{enquiry.organizationName}</span>
                    <span className="enquiry-tile__person">{enquiry.clientName}</span>
                </div>

                <StatusChip size="sm" tone={TONES[enquiry.status] ?? "neutral"} label={t.enquiries.statuses[enquiry.status]} />
            </header>

            <div className="enquiry-tile__body">
                {/* A number reverses its groups under Arabic, so it reads left to right of its own. */}
                <bdi className="enquiry-tile__phone" dir="ltr">
                    {enquiry.phone}
                </bdi>
                <span className="enquiry-tile__when">{formatDateTime(enquiry.createdAt, lang)}</span>
                <p className="enquiry-tile__message">{enquiry.message || t.enquiries.noMessage}</p>
            </div>

            <div className="enquiry-tile__actions">
                {/* The call is the whole job here — there is no other channel to this person. */}
                <a className="enquiry-tile__call" href={`tel:${enquiry.phone}`} onClick={(event) => event.stopPropagation()}>
                    <Icon name="phone" size={16} />
                    <span>{t.enquiries.call}</span>
                </a>

                <button
                    type="button"
                    className="enquiry-tile__convert"
                    onClick={(event) => {
                        event.stopPropagation();
                        onConvert();
                    }}
                >
                    <Icon name="add" size={16} />
                    <span>{t.enquiries.convert}</span>
                </button>
            </div>
        </article>
    );
}
